/**
 * Pure geometry for a sprite billboard — no three.js, no DOM, unit-testable
 * without a GPU (same split as `model-geometry.ts`).
 *
 * The original draws a sprite node by projecting its anchor, adding the
 * node's signed byte offsets and blitting the sheet frame at **1:1 pixels**
 * in a 320×200 frame — no perspective scaling (docs/formats/models-tbl.md
 * §4.3). `billboardQuad` below is that arithmetic with one free parameter,
 * `metresPerPixel`, because the two consumers disagree about what a sprite
 * pixel is worth and both are right:
 *
 * - **In the world** (`three/sprite-screen-size.ts`, `/fly`) a pixel is worth
 *   whatever makes the quad hold a *fixed screen size* at its own distance,
 *   which is the original's rule reproduced exactly. That factor changes
 *   every frame as the camera moves.
 * - **In the model viewer** a literal reproduction would show a burst that
 *   never changes size as you orbit, which tells you nothing about the
 *   object, so `metresPerSpritePixel` below gives it one fixed scale per
 *   object and lets the quad behave like the rest of the geometry.
 *
 * That viewer scale is a **convention, not a decode** — the file states no
 * world size for a sprite — but it is built from the engine's own two
 * quantities rather than invented: a LOD's `detail` is the apparent size in
 * pixels at which the engine switches to it (§3), and `boundingRadius` is
 * the object's world size, so at the last LOD one sprite pixel is worth
 * about `boundingRadius / detail` metres. That lands where you'd hope:
 * `FLAK`'s burst comes out ~37 m across (radius 30.8 m), `EXPLO`'s fireball
 * ~17 m, `SMOKE`'s puff ~1.2 m (radius 1.08 m).
 *
 * The obvious alternative — scale the widest frame to the bounding diameter —
 * was tried and rejected on `MISC#19`, the distant-aeroplane dot: its
 * `boundingRadius` of 10 000 units is a *cull range*, not a size, so a 3-pixel
 * dot rendered 100 m wide. Dividing by `detail` (255 for that object) gives it
 * a 4.8 m marker instead.
 */

/** The frame rect a baked billboard points at, within its sheet PNG. */
export interface SpriteFrameRectLike {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface BillboardLike {
  readonly position: readonly [number, number, number];
  readonly offsetX: number;
  readonly offsetY: number;
  readonly frame?: SpriteFrameRectLike;
}

export interface SheetSizeLike {
  readonly sheetWidth: number;
  readonly sheetHeight: number;
}

/** A camera-facing quad: where its centre sits relative to the anchor, how big it is, and which part of the sheet it shows. */
export interface BillboardQuad {
  /** Anchor in model space (metres) — the sprite node's own vertex. */
  readonly anchor: readonly [number, number, number];
  /** Quad centre offset from the anchor, in the billboard's own right/up axes, in metres. */
  readonly centerOffset: readonly [number, number];
  readonly width: number;
  readonly height: number;
  /** UV rect in three.js convention (v measured from the bottom of the image). */
  readonly uv: { readonly u0: number; readonly v0: number; readonly u1: number; readonly v1: number };
}

/** Fallback scale (one `.TBL` model unit per pixel) for an object with no usable `boundingRadius`/`detail` pair. */
const FALLBACK_METRES_PER_PIXEL = 1 / 65;

/**
 * Metres per sprite pixel for one object: `boundingRadius / detail` at the
 * object's most detailed LOD — see the module doc comment for why those two
 * numbers and not the frame size.
 */
export function metresPerSpritePixel(boundingRadius: number, maxDetail: number): number {
  if (boundingRadius <= 0 || maxDetail <= 0) return FALLBACK_METRES_PER_PIXEL;
  return boundingRadius / maxDetail;
}

/**
 * The same quad, flattened into a reusable record a caller can own for the
 * life of a pool. `/fly` re-derives every effect sprite's quad *every frame*
 * (the scale depends on the camera), so the allocating shape above would make
 * three objects per sprite per frame — exactly the garbage a 60 Hz loop should
 * not produce (`smoke-trail.ts` records the same rule for its puff pool).
 */
export interface MutableBillboardQuad {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  u0: number;
  v0: number;
  uSize: number;
  vSize: number;
}

export function emptyBillboardQuad(): MutableBillboardQuad {
  return { centerX: 0, centerY: 0, width: 0, height: 0, u0: 0, v0: 0, uSize: 0, vSize: 0 };
}

/**
 * Writes one baked billboard's quad into `target`, returning whether the
 * billboard has a drawable frame at all. The engine's offsets place the
 * frame's **top-left** corner, so the centre is half a frame further along
 * each axis; screen Y grows downward while the quad's up axis grows upward,
 * hence the sign flip on Y.
 *
 * The two axis scales are separate because a VGA mode-13h pixel is not square
 * (`flight/panel-layout.ts`'s `VGA_PIXEL_ASPECT`): a frame's *displayed* shape
 * is 1.2× taller than its raw pixel grid, so an in-world sprite passes a
 * narrower X scale to keep the art the shape a 1990 player saw. They default
 * to equal because the model viewer, which measures nothing against a screen,
 * has no aspect to correct for.
 */
export function writeBillboardQuad(
  target: MutableBillboardQuad,
  billboard: BillboardLike,
  metresPerPixel: number,
  sheet: SheetSizeLike,
  metresPerPixelX: number = metresPerPixel,
): boolean {
  const frame = billboard.frame;
  if (!frame || frame.width <= 0 || frame.height <= 0) return false;

  target.width = frame.width * metresPerPixelX;
  target.height = frame.height * metresPerPixel;
  target.centerX = (billboard.offsetX + frame.width / 2) * metresPerPixelX;
  target.centerY = -(billboard.offsetY + frame.height / 2) * metresPerPixel;
  target.u0 = frame.x / sheet.sheetWidth;
  target.uSize = frame.width / sheet.sheetWidth;
  // The baked strip stores every frame flush to the top (`y: 0`); three.js
  // measures v from the bottom, so the frame's top edge is the *higher* v.
  target.v0 = 1 - (frame.y + frame.height) / sheet.sheetHeight;
  target.vSize = frame.height / sheet.sheetHeight;
  return true;
}

/** `writeBillboardQuad` into a fresh record — the build-time shape, for a viewer that makes one mesh per sprite. */
export function billboardQuad(billboard: BillboardLike, metresPerPixel: number, sheet: SheetSizeLike): BillboardQuad | undefined {
  const quad = emptyBillboardQuad();
  if (!writeBillboardQuad(quad, billboard, metresPerPixel, sheet)) return undefined;
  return {
    anchor: billboard.position,
    centerOffset: [quad.centerX, quad.centerY],
    width: quad.width,
    height: quad.height,
    uv: { u0: quad.u0, u1: quad.u0 + quad.uSize, v0: quad.v0, v1: quad.v0 + quad.vSize },
  };
}
